import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useCreateCourseMutation } from '@/features/editor/coursesApi'
import { useMe } from '@/features/auth/useMe'
import type { CourseCreate } from '@/types/course'
import type { Lang } from '@/types/lang'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

/**
 * Курс создаётся БЕЗ названия: «Без названия» студия показывает серой
 * подсказкой (`untitledLabel`), а не пишет в данные — иначе подставленный
 * текст считался бы заполненным названием, и проверка публикации его не ловила.
 */
const EMPTY_NAMES: Pick<CourseCreate, 'name' | 'nameEn'> = { name: '', nameEn: null }

/**
 * Запасной вариант, если сервер не примет пустое название. Плейсхолдер кладётся
 * в слот ЯЗЫКА ПРОФИЛЯ, а не всегда в базовый: язык курса, созданного руками,
 * бэк берёт из профиля (у автора с languageCode: 'en' русский текст в базовом
 * слоте сделал бы курс «уже переведённым» на русский).
 */
const namesFor = (lang: Lang | null | undefined): Pick<CourseCreate, 'name' | 'nameEn'> =>
  lang === 'en' ? { name: '', nameEn: 'New course' } : { name: 'Новый курс', nameEn: null }

const DEFAULT_COURSE: Omit<CourseCreate, 'name' | 'nameEn'> = {
  description: null,
  descriptionEn: null,
  banner: null,
  color: null,
  difficulty: null,
  usdPrice: null,
  priceRub: null,
  basePrice: null,
  rewardScForSharing: 0,
  isPublished: false,
  isPublic: true,
  showInExplore: false,
  trialLessonsCount: 0,
  isHiddenOnWeb: false,
  isHiddenOnIos: false,
  isHiddenOnAndroid: false,
  isHiddenOnTma: false,
  isAvailableBySubscription: false,
  giveawayId: null,
  bonuses: null,
  categoryIds: [],
}

/**
 * Creates a course with default fields and navigates to its editor.
 * Used by the Dashboard "Create" button — the Home landing now starts a
 * generation job instead of an empty course (see useStartGeneration).
 */
export const useCreateCourse = () => {
  const navigate = useNavigate()
  const { data: me } = useMe()
  const [createCourse, { isLoading: isCreating }] = useCreateCourseMutation()

  const handleCreate = async () => {
    try {
      const created = await createCourse({ ...DEFAULT_COURSE, ...EMPTY_NAMES })
        .unwrap()
        .catch(() => createCourse({ ...DEFAULT_COURSE, ...namesFor(me?.languageCode) }).unwrap())
      navigate(`/editor/${created.id}`)
    } catch {
      toast.error(messagesNow(messages).createCourseFailed)
    }
  }

  return { handleCreate, isCreating }
}
